import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const CATEGORIES = ["Startup & Business", "Registrations & Compliance", "Finance", "Legal & IP", "Branding & Design", "Technology", "Website", "Mobile App", "Software", "Procurement", "Marketing", "Sales", "HR", "Operations", "Consulting"];
const SPECIALIST_CATEGORIES = ["CA Specialist", "CS Specialist", "Legal Specialist", "Purchase Manager", "Marketing Specialist", "Design Manager", "Software / Technology Specialist", "Website Specialist", "Mobile App Specialist", "Business Consultant", "Finance Specialist", "HR Specialist", "Sales Specialist", "Operations Specialist", "Compliance Specialist", "Other"];

const EMPTY = {
  name: "", category: "Registrations & Compliance", description: "", who_needs: "", price: 0,
  gst_percent: 18, documents_required: "", workflow: "", specialist_category: "CA Specialist",
  processing_days: "", terms: "", active: true, display_order: 0,
};

export function ServicesModule() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/portal/admin/services").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openForm = (item) => {
    setEditing(item || null);
    setForm(
      item
        ? { ...item, documents_required: (item.documents_required || []).join("\n"), workflow: (item.workflow || []).map((w) => w.name).join("\n") }
        : { ...EMPTY }
    );
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0, gst_percent: Number(form.gst_percent) || 0, display_order: Number(form.display_order) || 0 };
      if (editing?.id) {
        await api.put(`/portal/admin/services/${editing.id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/portal/admin/services", payload);
        toast.success("Service created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete service "${item.name}"?`)) return;
    try {
      await api.delete(`/portal/admin/services/${item.id}`);
      toast.success("Service deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.put(`/portal/admin/services/${item.id}`, { ...item, active: !item.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-services-module">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Services Master</h2>
          <p className="text-sm text-slate-400">{items.length} services · all prices admin-controlled, never hardcoded</p>
        </div>
        <Button data-testid="service-add" onClick={() => openForm(null)} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Add Service
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((sv) => (
            <div key={sv.id} className="glass rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`service-row-${sv.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display font-semibold text-white">{sv.name}</p>
                  <span className="text-[10px] font-mono-tech px-2 py-0.5 rounded-full border border-[#00B0FF]/40 text-[#00B0FF]">{sv.category}</span>
                  <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${sv.active ? "border-[#00E676]/40 text-[#00E676]" : "border-slate-500/40 text-slate-500"}`}>{sv.active ? "ACTIVE" : "INACTIVE"}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">₹{sv.price.toLocaleString("en-IN")} + {sv.gst_percent}% GST · {sv.specialist_category} · {sv.processing_days} · {(sv.workflow || []).length} workflow steps · {(sv.documents_required || []).length} documents</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={sv.active} onCheckedChange={() => toggleActive(sv)} data-testid={`service-active-${sv.id}`} />
                <Button size="icon" variant="outline" data-testid={`service-edit-${sv.id}`} onClick={() => openForm(sv)} className="border-white/15 text-slate-300 hover:bg-white/5"><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="outline" data-testid={`service-delete-${sv.id}`} onClick={() => remove(sv)} className="border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="bg-[#0D111D] border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader>
            <DialogDescription className="sr-only">Service form</DialogDescription>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Service" : "Add Service"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Service Name *</Label>
                <Input data-testid="service-field-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#06080E] border-white/10" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="service-field-category" className="bg-[#06080E] border-white/10 text-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200 max-h-56">
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Price (₹)</Label>
                <Input data-testid="service-field-price" type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-[#06080E] border-white/10" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">GST %</Label>
                <Input data-testid="service-field-gst" type="number" min="0" max="40" value={form.gst_percent} onChange={(e) => set("gst_percent", e.target.value)} className="bg-[#06080E] border-white/10" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Specialist Category</Label>
                <Select value={form.specialist_category} onValueChange={(v) => set("specialist_category", v)}>
                  <SelectTrigger data-testid="service-field-specialist" className="bg-[#06080E] border-white/10 text-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200 max-h-56">
                    {SPECIALIST_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Processing Time</Label>
                <Input data-testid="service-field-processing" value={form.processing_days} onChange={(e) => set("processing_days", e.target.value)} placeholder="e.g. 5–7 working days" className="bg-[#06080E] border-white/10" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea data-testid="service-field-description" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-[#06080E] border-white/10" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Who needs it</Label>
              <Input data-testid="service-field-who" value={form.who_needs} onChange={(e) => set("who_needs", e.target.value)} className="bg-[#06080E] border-white/10" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Required Documents (one per line)</Label>
              <Textarea data-testid="service-field-documents" rows={4} value={form.documents_required} onChange={(e) => set("documents_required", e.target.value)} className="bg-[#06080E] border-white/10" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Workflow Steps (one per line — the specialist's task engine)</Label>
              <Textarea data-testid="service-field-workflow" rows={5} value={form.workflow} onChange={(e) => set("workflow", e.target.value)} className="bg-[#06080E] border-white/10" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Terms</Label>
              <Textarea data-testid="service-field-terms" rows={2} value={form.terms} onChange={(e) => set("terms", e.target.value)} className="bg-[#06080E] border-white/10" />
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="service-field-active" checked={form.active} onCheckedChange={(v) => set("active", v)} />
              <span className="text-sm text-slate-400">Active (visible in catalog)</span>
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-white/15 text-slate-300 hover:bg-white/5">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="service-save" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">{saving ? "Saving..." : "Save Service"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
